// src/hooks/useTasks.ts - 使用 IndexedDB 版本
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { logError } from '../utils/logger';
import { Task, isVisibleTask } from '../types/task';
import { taskService } from '../features/task';
import {
  saveTasks,
  getAllTasks as getTasksFromIndexedDB,
  cleanupExpiredTasks,
  getTaskCacheStats
} from '../services/indexedDB';
import { taskRepository } from '../services/indexedDB/repositories/taskRepository';
import { eventBus, EVENTS } from '../utils/eventBus';

// 任務管理 Hook - 支持即時監聽 + IndexedDB 快取
export const useTasks = (userId: string) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Map<string, boolean>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // 使用 ref 追蹤狀態，避免重複請求和訂閱
  const dataLoadedRef = useRef(false);
  // IndexedDB 是否回報有快取資料，用於避免訂閱 effect 覆蓋 isLoading(false)
  const hasLocalCacheRef = useRef(false);
  // realtime listener 是否已收到第一次 snapshot，防止 IndexedDB 非同步讀取覆蓋即時資料
  const realtimeReceivedRef = useRef(false);
  const realtimeUnsubscribeRef = useRef<(() => void) | null>(null);
  const [useRealtime, setUseRealtime] = useState(true);
  const fallbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupInitializedRef = useRef(false);
  
  // IndexedDB 存儲操作：upsert 傳入的任務，不清除 CLOSED 快取（保留樂觀更新結果）
  const saveTasksToIndexedDB = useCallback(async (tasksToSave: Task[]) => {
    try {
      await saveTasks(userId, tasksToSave);
    } catch (error) {
      logError('保存任務到 IndexedDB 失敗', error);
    }
  }, [userId]);

  const loadTasksFromIndexedDB = useCallback(async (): Promise<Task[] | null> => {
    try {
      const tasks = await getTasksFromIndexedDB(userId);
      // 只取 OPEN 任務供 useTasks 管理；CLOSED 保留在 IndexedDB 供其他 hook（如 useRecentTasks）查詢
      const open = tasks.filter(t => t.status !== 'CLOSED');
      return open.length > 0 ? open : null;
    } catch (error) {
      logError('從 IndexedDB 加載任務失敗', error);
      return null;
    }
  }, [userId]);

  // 背景清理過期數據
  const performBackgroundCleanup = useCallback(async () => {
    if (!userId || cleanupInitializedRef.current) return;

    try {
      await cleanupExpiredTasks(userId, 30); // 30 天
      await taskRepository.pruneClosedTasks(userId, 30); // CLOSED task 上限 30 筆
      cleanupInitializedRef.current = true;
    } catch (error) {
      logError('背景清理失敗', error);
    }
  }, [userId]);

  // 差異更新處理函數
  const handleTaskChanges = useCallback(async (newTasks: Task[]) => {
    realtimeReceivedRef.current = true;

    // Firestore listener 只推送 OPEN 任務。
    // 將 IndexedDB 中仍為 OPEN、但不在新清單裡的任務補標為 CLOSED，
    // 讓後續讀取 IndexedDB 的 hook（如 useRecentTasks）能拿到正確狀態。
    const syncStaleToClosed = async () => {
      try {
        const allCached = await getTasksFromIndexedDB(userId);
        const newOpenIds = new Set(newTasks.map(t => t.id));
        const staleOpenTasks = allCached.filter(
          t => t.status === 'OPEN' && !newOpenIds.has(t.id)
        );
        for (const stale of staleOpenTasks) {
          await taskRepository.createLocal({
            ...stale,
            status: 'CLOSED',
            closedAt: stale.closedAt ?? new Date(),
            updatedAt: new Date(),
          });
        }
      } catch (err) {
        logError('同步 stale OPEN task 為 CLOSED 失敗', err);
      }
    };

    setTasks(prevTasks => {
      // 檢查是否真的有變化，避免不必要的重渲染
      if (prevTasks.length === newTasks.length) {
        const hasChanges = newTasks.some((newTask, index) => {
          const prevTask = prevTasks[index];
          if (!prevTask || prevTask.id !== newTask.id) {
            return true;
          }

          // 檢查關鍵欄位變化
          if (prevTask.updatedAt?.getTime() !== newTask.updatedAt?.getTime()) return true;
          if (prevTask.status !== newTask.status) return true;

          // 檢查 scheduledTimeBoxIds 變化（排程狀態）
          const prevScheduled = prevTask.scheduledTimeBoxIds || [];
          const newScheduled = newTask.scheduledTimeBoxIds || [];
          if (prevScheduled.length !== newScheduled.length) return true;
          if (prevScheduled.some((id, i) => id !== newScheduled[i])) return true;

          return false;
        });

        if (!hasChanges) {
          return prevTasks; // 沒有變化，返回原狀態
        }
      }

      // 按創建時間排序任務 (與 Firestore 查詢一致)
      const sortedTasks = [...newTasks].sort((a, b) => {
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      // 異步保存到 IndexedDB（不阻塞 UI）
      saveTasksToIndexedDB(sortedTasks).catch(error => {
        logError('異步保存任務失敗', error);
      });

      // 背景補標 stale OPEN → CLOSED（不阻塞 UI 更新）
      syncStaleToClosed();

      return sortedTasks;
    });
  }, [saveTasksToIndexedDB, userId]);

  // 啟動即時監聽
  const startRealtimeListener = useCallback(async () => {
    if (!userId || realtimeUnsubscribeRef.current) return;
    
    try {
      
      const unsubscribe = taskService.subscribeToTasks(userId, {
        onSnapshot: handleTaskChanges,
        onError: (error) => {
          logError('即時監聽失敗，切換到定時查詢', error);
          setError(error);
          setUseRealtime(false); // 降級到定時查詢
        }
      });
      
      realtimeUnsubscribeRef.current = unsubscribe;
      setError(null); // 成功連接，清除錯誤
      
    } catch (error) {
      logError('啟動即時監聽失敗', error);
      setError(error instanceof Error ? error : new Error('即時監聽啟動失敗'));
      setUseRealtime(false);
    }
  }, [userId, handleTaskChanges]);

  // 停止即時監聽
  const stopRealtimeListener = useCallback(() => {
    if (realtimeUnsubscribeRef.current) {
      realtimeUnsubscribeRef.current();
      realtimeUnsubscribeRef.current = null;
    }
  }, []);

  // 啟動定時查詢（降級方案）
  const startFallbackTimer = useCallback(() => {
    if (fallbackIntervalRef.current) return;
    
    
    const fetchTasksPeriodically = async () => {
      if (!document.hidden) { // 只在頁面可見時查詢
        try {
          const allTasks = await taskService.getAllTasks(userId);
          handleTaskChanges(allTasks);
        } catch (error) {
          logError('定時查詢失敗', error);
        }
      }
    };
    
    // 立即執行一次
    fetchTasksPeriodically();
    
    // 每2分鐘查詢一次
    fallbackIntervalRef.current = setInterval(fetchTasksPeriodically, 2 * 60 * 1000);
  }, [userId, handleTaskChanges]);

  // 停止定時查詢
  const stopFallbackTimer = useCallback(() => {
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
  }, []);

  // 手動刷新功能
  const refreshTasks = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setError(null);
    
    try {
      const allTasks = await taskService.getAllTasks(userId);
      await handleTaskChanges(allTasks);
      
      // 如果即時監聽被禁用，嘗試重新啟用
      if (!useRealtime) {
        setUseRealtime(true);
      }
      
    } catch (error) {
      logError('手動刷新失敗', error);
      setError(error instanceof Error ? error : new Error('刷新任務失敗'));
    } finally {
      setIsRefreshing(false);
    }
  }, [userId, isRefreshing, handleTaskChanges, useRealtime]);

  // 初始化：從 IndexedDB 加載 + 背景清理
  useEffect(() => {
    if (!userId || dataLoadedRef.current) return;
    
    const initializeTasks = async () => {
      try {
        // 1. 從 IndexedDB 加載本地快取
        const localTasks = await loadTasksFromIndexedDB();
        if (localTasks && localTasks.length > 0) {
          hasLocalCacheRef.current = true;
          // 若 realtime listener 已先收到資料，不覆蓋（避免 CLOSED task 污染）
          if (!realtimeReceivedRef.current) {
            setTasks(localTasks);
          }
          // 有本地快取時立即關掉 loading，讓用戶先看到資料
          // Firestore 的即時監聽會在背景繼續更新
          setIsLoading(false);
        }

        // 2. 背景清理過期數據（非阻塞）
        performBackgroundCleanup();

      } catch (error) {
        logError('初始化任務失敗', error);
      }
    };
    
    initializeTasks();
    dataLoadedRef.current = true;
  }, [userId, loadTasksFromIndexedDB, performBackgroundCleanup]);

  // 主要數據訂閱邏輯
  useEffect(() => {
    if (!userId) return;

    // 只在無 IndexedDB 快取時才顯示 loading，避免有快取時出現閃爍
    if (!hasLocalCacheRef.current) {
      setIsLoading(true);
    }

    if (useRealtime) {
      // 嘗試即時監聽
      startRealtimeListener().finally(() => {
        setIsLoading(false);
      });
    } else {
      // 使用定時查詢降級方案
      startFallbackTimer();
      setIsLoading(false);
    }

    return () => {
      stopRealtimeListener();
      stopFallbackTimer();
    };
  }, [userId, useRealtime, startRealtimeListener, startFallbackTimer, stopRealtimeListener, stopFallbackTimer]);

  // 訂閱 timebox 刪除事件，從 IndexedDB 立即同步 task 狀態
  // （刪除流程在 Firestore 寫入前已更新 IndexedDB，不需要等 realtime listener）
  useEffect(() => {
    if (!userId) return;

    const handleTimeBoxDeleted = async () => {
      const localTasks = await loadTasksFromIndexedDB();
      if (localTasks) {
        setTasks(localTasks);
      }
    };

    eventBus.on(EVENTS.TIMEBOX.DELETED, handleTimeBoxDeleted);
    return () => {
      eventBus.off(EVENTS.TIMEBOX.DELETED, handleTimeBoxDeleted);
    };
  }, [userId, loadTasksFromIndexedDB]);

  // 獲取開放任務（tasks state 已是 OPEN-only，直接返回）
  const getOpenTasks = useCallback((limit?: number) => {
    return limit !== undefined ? tasks.slice(0, limit) : tasks;
  }, [tasks]);
  
  // 任務選擇相關功能（保持不變）
  const toggleTaskSelection = useCallback((taskId: string, selected: boolean) => {
    setSelectedTasks(prev => {
      const newMap = new Map(prev);
      if (selected) {
        newMap.set(taskId, true);
      } else {
        newMap.delete(taskId);
      }
      return newMap;
    });
  }, []);
  
  const getSelectedTasksArray = useCallback(() => {
    return Array.from(selectedTasks.entries()).map(([id, must]) => ({
      id,
      must
    }));
  }, [selectedTasks]);
  
  const resetSelections = useCallback(() => {
    setSelectedTasks(new Map());
  }, []);

  // 新增：獲取快取統計信息（調試用）
  const getCacheStats = useCallback(async () => {
    try {
      return await getTaskCacheStats(userId);
    } catch (error) {
      logError('獲取快取統計失敗', error);
      return null;
    }
  }, [userId]);

  // 排除已 Drop 的任務（closeReason: 'dropped'），供 UI 層直接使用
  const visibleTasks = useMemo(() => tasks.filter(isVisibleTask), [tasks]);

  return {
    // 原有接口（完全兼容）
    tasks,
    visibleTasks,
    selectedTasks,
    isLoading,
    error,
    isRefreshing,
    useRealtime,
    refreshTasks,
    getOpenTasks,
    toggleTaskSelection,
    getSelectedTasksArray,
    resetSelections,
    // 向後兼容：保留 fetchTasks 作為別名
    fetchTasks: refreshTasks,
    
    // 新增：調試和管理功能
    getCacheStats, // 獲取快取統計
  };
};